import React, { useContext, type ReactNode } from 'react';
import { styled as styled2 } from '@compiled/react';
// eslint-disable-next-line jira/restricted/styled-components-migration, @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from 'styled-components';
import Button from '@atlaskit/button';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import HelpPanelButton from '@atlassian/jira-help-panel-button';
import { FormattedMessage } from '@atlassian/jira-intl';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type { FormatMessage } from '@atlassian/jira-shared-types/src/general.tsx';
import permissionsGateImage from '../../../assets/permissions-gate.svg';
import {
	UPGRADE_URL,
	LEARN_MORE_LINK,
	ADMIN_USER_MANAGEMENT_LINK,
} from '../../../common/constants';
import type { AccessPageContext } from '../../../common/types';
import MainProviderContext from '../../../services/access-page-context';
import messages from './messages';
import type { Props } from './types';
import { fireAnalyticsBasedOnType } from './utils';

const getFormatMessageProps = (
	baseUrl: string,
	isSiteAdmin: boolean,
	formatMessage: FormatMessage,
) => {
	if (isSiteAdmin) {
		return {
			...messages.addPeopleAdminDescription,
			values: {
				adminSettingsLink: (
					<Button
						href={baseUrl + ADMIN_USER_MANAGEMENT_LINK}
						target="_blank"
						appearance="link"
						onClick={fireAnalyticsBasedOnType('AdminSettings')}
						spacing="none"
					>
						{formatMessage(messages.administerSettings)}
					</Button>
				),
			},
		};
	}
	return { ...messages.addPeopleDescription };
};

export const PermissionGateView = (props: Props) => {
	const {
		intl: { formatMessage },
	} = props;
	const context: AccessPageContext = useContext(MainProviderContext);
	const { baseUrl, isSiteAdmin } = context;
	return (
		<ImageWrapper data-testid="users-and-roles.ui.actors-table.permission-gate-view.wrapper">
			<img alt="Project roles not configurable" src={permissionsGateImage} width={gridSize * 18} />
			<HeaderWrapper>{formatMessage(messages.header)}</HeaderWrapper>

			<TextAndLinkWrapper>
				<FormatMessageWrapper>
					<FormattedMessage {...getFormatMessageProps(baseUrl, isSiteAdmin, formatMessage)} />
				</FormatMessageWrapper>
				<ButtonWrapper>
					<Button
						href={context.baseUrl + UPGRADE_URL}
						onClick={fireAnalyticsBasedOnType('Upgrade')}
						target="_blank"
						appearance="link"
						spacing="none"
					>
						{formatMessage(messages.upgradePlan)}
					</Button>
					{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
					<span>•</span>
					<HelpPanelButton
						articleId="lCKOAk8httUpZvITNpBBw"
						appearance="link"
						spacing="none"
						fallbackComponent={
							<Button
								href={LEARN_MORE_LINK}
								onClick={fireAnalyticsBasedOnType('LearnMore')}
								target="_blank"
								appearance="link"
								spacing="none"
							>
								{formatMessage(messages.learnMoreAboutPlans)}
							</Button>
						}
					>
						{formatMessage(messages.learnMoreAboutPlans)}
					</HelpPanelButton>
				</ButtonWrapper>
			</TextAndLinkWrapper>
		</ImageWrapper>
	);
};

export default injectIntl(PermissionGateView);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ImageWrapper = styled2.div<{
	children?: ReactNode;
	['data-testid']: string;
}>({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	marginTop: `${gridSize * 15}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TextAndLinkWrapper = styled2.div<{
	children?: ReactNode;
}>({
	margin: token('space.100', '8px'),
	display: 'flex',
	flexDirection: 'column',
	textAlign: 'center',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${gridSize * 43}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonWrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.link', colors.B400),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'> span': {
		padding: `0 ${token('space.100', '8px')}`,
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FormatMessageWrapper = styled2.div<{
	children?: ReactNode;
}>({
	padding: `0 0 ${token('space.100', '8px')} 0`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderWrapper = styled2.h2<{
	children?: ReactNode;
}>({
	marginTop: token('space.300', '24px'),
});
